import axios from 'axios';
import { extend, stripTags } from '../utilities/utilities';
import menuTemplate from './mobileMenu.template.hbs';
import paneTemplate from './mobileMenu.pane.template.hbs';


/**
 * Mobile menu
 * Javascript functionality for a (mobile) menu.
 * @version 3.0.0
 * @exports MobileMenu
 */
export default class MobileMenu {
    /**
     * Constructor
     * @param {Object} el - The page main menu as a DOM node.
     * @param {Object} options - Contains options for the menu.
     * @public
     */
    constructor(el, options) {
        /** Boolean indicating if the search-field should be rendered. */
        this.searchField = false;

        /** String with prefered search method. */
        this.searchMethod = 'GET';

        /** String with url to search page. */
        this.searchPage = '/search';

        /** String with the parameter name for searching. */
        this.searchParameter = 'query';

        /** String with the placeholder text for the search-field. */
        this.searchPlaceholder = 'Search';

        /** Object which will contain all panes in the menu. */
        this.menuPages = {};

        /** Placeholder for the pane holder. */
        this.paneHolder = null;

        /** Placeholder for the initial active menu pane. */
        this.activePane = null;

        /** Placeholder for the ajax path (if the menu should run with ajax). */
        this.ajaxPath = null;

        /** String with the id of the active pane (used for the initial ajax request). */
        this.ajaxActivePane = 'root';

        /** String with the active-class for page menu items. */
        this.activeClass = 'is-active';

        /** The page-container DOM element. */
        this.pageContainer = document.getElementById('page-container-full');
        // page-container-full används alltid?
        //this.pageContainer = window.matchMedia('(max-width: 767px)').matches
        //    ? document.getElementById('page-container')
        //    : document.getElementById('page-container-full');

        /** Placeholder for menu DOM element. */
        this.menuNode = null;

        /** opened once on mobile flag */
        this.openedOnceOnMobile = false;

        /** Placeholder for the menu cover DOM element (covers the page when the menu is opened). */
        this.menuCoverNode = null;

        /** The menu button DOM element. */
        this.menuButtonNode = document.getElementById('menu-button');

        /** The close menu button DOM element. */
        this.closeMenuButtonNode = null; //document.getElementById('menu-button-close');

        extend(this, options);

        this.el = el;

        if (this.menuButtonNode) {
            this._init();
        }
    }

    /**
     * The initialization function for this module.
     * @private
     */
    _init() {
        const body = document.body;

        // Menu closed by default states.
        // Should be closed on: Mobile, edit and when user has closed the menu.
        if (window.innerWidth < 1600 || body.classList.contains('edit-mode') || sessionStorage.getItem('hasMenuOpenedBefore') || body.dataset.menuCollapsed?.toLowerCase() === 'true') {

            body.classList.remove('nav-open', 'nav-is-visible', 'nav-opened');

            const menuText = document.querySelector('.page-header__mobile-menu--text');
            menuText.innerHTML = 'MENY';

            const headerLogo = document.querySelector('.page-header__mobile-menu--home-link');
            headerLogo.classList.remove('menu-opened-logo');

            const menuButton = document.querySelector('.page-header__mobile-menu');
            menuButton.classList.remove('menu-opened');

            const menuBar = document.querySelector('.page-header__menubar');
            menuBar.classList.remove('menu-scrolled');

            this.menuButtonNode.setAttribute('aria-expanded', false);
            document.getElementById('page-container-full').classList.remove('mobile-menu-slideout');
        }

        this._renderMenu();

        this.menuButtonNode.setAttribute('href', '#mobile-menu');

        if (!this.ajaxPath) {
            try {
                this._parseMenu(this.el);
            } catch (e) { }
        } else {
            this._initAjax();
        }

        this._initEvents();
    }

    /**
     * Initiate ajax.
     * @private
     */
    _initAjax() {
        this._loadMenu(this.ajaxActivePane);
    }

    /**
     * 
     * @private
     * */
    _initEvents() {
        this.menuButtonNode.addEventListener('click', event => {
            event.preventDefault();
            this.openMenu();
        });

        document.addEventListener('click', event => {
            let link = event.target;

            if (link.classList.contains('mobile-menu__topbar__close')) {
                event.preventDefault();
                this.toggleMenu();
            }

            if (link.tagName === 'SPAN') {
                link = link.parentElement;
            }

            // Normal navigation
            if (link.classList.contains('mobile-menu-nav')) {
                event.preventDefault();
                const linkTarget = document.getElementById(link.getAttribute('aria-controls'));
                if (linkTarget) {
                    this._openMenuPane(linkTarget);
                } else if (this.ajaxPath) {
                    link.classList.add('is-loading');
                    link.setAttribute('aria-controls', 'mobile-menu-pane-' + link.getAttribute('data-target'));
                    this._loadMenu(link.getAttribute('data-target'));
                } else {
                    alert('An error occured, try again later.');
                }
            }
        });
    }

    checkIfStickymenuIsActive() {
        const isDesktop = window.innerWidth > 1599;
        const stickyMenuActive = document.getElementById("StickyMenuOnDesktop");
        const menuIsOpen = this.getCookie("setMenuSticky");

        return isDesktop && stickyMenuActive && menuIsOpen;
    }

    checkStickyOpenMenu() {
        if (this.checkIfStickymenuIsActive()) {
            this.openMenu(true);
        }
    }

    setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

        document.cookie =
            `${name}=${encodeURIComponent(value)}; ` +
            `expires=${date.toUTCString()}; path=/`;
    }

    getCookie(name) {
        const cookies = document.cookie.split("; ");

        for (const cookie of cookies) {
            const [key, value] = cookie.split("=");

            if (key === name) {
                return decodeURIComponent(value);
            }
        }

        return null;
    }

    deleteCookie(name) {
        document.cookie =
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }

    /**
     * Opens the menu.
     * @public
     */
    openMenu(isStickyMenu = false) {

        this.setCookie("setMenuSticky", "true", 30);

        if (this.addVisibleClassInterval) {
            clearTimeout(this.addVisibleClassInterval);
        }

        const mobileClose = document.querySelector('.mobile-menu__topbar__close');
        mobileClose.setAttribute('aria-expanded', true);

        this.pageContainer.classList.add(isStickyMenu ? 'mobile-menu-popout' : 'mobile-menu-slideout');
        this.menuNode.setAttribute('aria-hidden', false);
        this.menuButtonNode.setAttribute('aria-expanded', true);

        const body = document.body;
        body.classList.add('nav-open', 'nav-is-visible');
        body.classList.remove('search-open');

        this.addClassInterval = setTimeout(() => {
            body.classList.add('nav-opened');
            this.menuNode.setAttribute('tabIndex', -1);
            this.menuNode.focus();
        }, 100);

        setTimeout(() => {
            body.classList.remove('search-is-visible');
            document.getElementById('page-container').classList.remove('mobile-menu-slideout');
        }, 400);

        const pageWrapper = document.querySelector('.page-wrapper');
        pageWrapper.style.margin = 0;

        //var menuText = document.querySelector('.page-header__mobile-menu--text');
        //menuText.innerHTML = 'STÄNG';
        //menuButton.setAttribute('style', 'display: none;');

        const menuButton = document.querySelector('.page-header__mobile-menu');
        menuButton.classList.add('menu-opened');

        const headerLogo = document.querySelector('.page-header__mobile-menu--home-link');
        headerLogo.classList.add('menu-opened-logo');

        // var menuBar = document.querySelector('.page-header__menubar');
        // menuBar.classList.add('menu-scrolled');

        document.getElementById('page-container').style.marginTop = '0';

        // remove user closed menu flag
        if (window.innerWidth > 786 && !body.classList.contains('edit-mode')) {
            sessionStorage.removeItem('hasMenuOpenedBefore');
        }
    }

    /*
     * Closes the menu.
     * @public
     */
    closeMenu() {

        this.deleteCookie("setMenuSticky");
        const body = document.body;
        body.classList.remove('nav-open', 'nav-opened');

        if (this.addClassInterval) {
            clearTimeout(this.addClassInterval);
        }

        this.menuNode.setAttribute('aria-hidden', true);
        this.menuButtonNode.setAttribute('aria-expanded', false);

        this.addVisibleClassInterval = setTimeout(() => {
            document.body.classList.remove('nav-is-visible');
            this.pageContainer.classList.remove('mobile-menu-slideout');
        }, 400);

        const pageWrapper = document.querySelector('.page-wrapper');
        pageWrapper.style.removeProperty('margin');

        //var menuText = document.querySelector('.page-header__mobile-menu--text');
        //menuText.innerHTML = 'MENY';
        //menuButton.setAttribute('style', 'display: block;');

        const menuButton = document.querySelector('.page-header__mobile-menu');
        menuButton.classList.remove('menu-opened');

        const headerLogo = document.querySelector('.page-header__mobile-menu--home-link');
        headerLogo.classList.remove('menu-opened-logo');

        // var menuBar = document.querySelector('.page-header__menubar');
        // menuBar.classList.remove('menu-scrolled');

        // Set user closed menu flag
        if (window.innerWidth > 786 && !body.classList.contains('edit-mode')) {
            sessionStorage.setItem('hasMenuOpenedBefore', true);
        }
    }

    /**
     * Toggles the menu.
     * @public
     */
    toggleMenu() {
        if (window.innerWidth > 786 && sessionStorage.getItem('hasMenuOpenedBefore') === 'true') {
            this.menuNode.setAttribute('aria-hidden', true);
        }

        this.menuNode.getAttribute('aria-hidden') === 'true' ? this.openMenu() : this.closeMenu();
    }

    /**
     * Renders the mobile menu html.
     * @private
     */
    _renderMenu() {
        const menuHtml = menuTemplate({
            searchField: this.searchField,
            searchMethod: this.searchMethod,
            searchPage: this.searchPage,
            searchParameter: this.searchParameter,
            searchPlaceholder: this.searchPlaceholder,
        });

        document.body.insertAdjacentHTML('BeforeEnd', menuHtml);
        this.menuNode = document.getElementById('mobile-menu');
        this.menuCoverNode = document.getElementById('mobile-menu-page-cover');
        this.paneHolder = document.getElementById('mobile-menu-paneholder');

        this.checkStickyOpenMenu();
    }

    /**
     * Renders the html for a pane.
     * @private
     */
    _renderMenuPane(pane, place) {
        const paneHtml = paneTemplate({ mainConfig, ...pane });
        this.paneHolder.insertAdjacentHTML(place || 'BeforeEnd', paneHtml);
    }

    /**
     * Parses the page menu and create mobile menu panes.
     * @param {Object} el - The page main menu as a DOM node.
     * @private
     */
    _parseMenu(el) {
        const html = stripTags(el.innerHTML, '<ul><li><a>');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const jsonData = { items: this._parsePageMenu(doc.querySelector('ul')) };

        this._generateMenuPages(jsonData.items, 'root');

        for (const key in this.menuPages) {
            if (this.menuPages.hasOwnProperty(key)) {
                this._renderMenuPane(this.menuPages[key]);
            }
        }

        if (!this.activePane) {
            this.activePane = 'root';
        }

        this._openMenuPane('mobile-menu-pane-' + this.activePane);
    }

    /**
     * Recursivly parses the page menu and collect all ul/li/a.
     * @param {Object} el - A DOM node to parse.
     * @param {Object} parentObj - The parent DOM node.
     * @private
     */
    _parsePageMenu(obj, parentObj) {

        const json = [];

        for (let child = obj.firstChild; child; child = child.nextSibling) {
            if (child !== 1) {
                continue;
            }
            const tag = {
                node: child.tagName,
                classlist: child.getAttribute('class') || '',
            };
            if (child.tagName === 'A') {
                tag.text = child.textContent || child.innerText;
                tag.url = child.getAttribute('href');
                parentObj.link = tag;
            } else {
                tag.ul = child.tagName === 'UL';
                tag.items = this._parsePageMenu(child, tag);
                json.push(tag);
            }
        }

        return json;
    }

    /**
     * Generate panes for each submenu (recursive).
     * @param {Object[]} arr - Array of items.
     * @param {Object} itemParent - The parent item.
     * @param {Object} paneParent - The parent pane item.
     * @private
     */
    _generateMenuPages(arr, itemParent, paneParent, parentItemName, parentItemUrl) {
        let currentName = '';
        let currentUrl = null;
        let i = 0;

        for (const item of arr) {
            if (item.node === 'LI') {
                item.parentId = itemParent;
                item.currentId = itemParent + '-' + i;

                let page = this.menuPages[itemParent];

                if (!page) {
                    page = this.menuPages[itemParent] = {
                        currentId: itemParent,
                        parentId: paneParent || null,
                        parentName: parentItemName || '',
                        parentUrl: parentItemUrl || null,
                        items: [],
                    };
                }

                page.items.push(item);

                if (item.link) {
                    currentName = item.link.text;
                    currentUrl = item.link.url;
                    if (item.link.classlist.contains(this.activeClass)) {
                        this.activePane = itemParent;
                    }
                }
            } else {
                item.currentId = itemParent;
                itemParent = paneParent;
                currentName = parentItemName;
                currentUrl = parentItemUrl;
            }

            if (item.items.length) {
                this._generateMenuPages(item.items, item.currentId, itemParent, currentName, currentUrl);
            }

            i++;
        }
    }

    /**
     * Opens a submenu (pane).
     * @param {Object} pane - The pane to be opened as a DOM node.
     * @private
     */
    _openMenuPane(pane) {

        if (!pane) {
            return;
        }

        if (typeof pane === 'string') {
            pane = document.getElementById(pane);
        }

        const activePane = this.menuNode.querySelector('.mobile-menu__pane--active');
        //let headerLogo = document.querySelector('.mobile-menu__topbar__logo-link'),

        if (activePane !== null) {
            activePane.setAttribute('aria-hidden', true);
        }

        for (const activeMenuLink of this.menuNode.querySelectorAll('[aria-expanded="true"]')) {
            activeMenuLink.setAttribute('aria-expanded', false);
        }

        pane.setAttribute('aria-hidden', false);

        const targets = document.querySelectorAll('[data-target="' + pane.id + '"]');
        targets.forEach(link => link.setAttribute('aria-expanded', true));

        let ancestor = document.getElementById('mobile-menu-pane-' + pane.getAttribute('data-parent'));

        while (ancestor !== null) {
            ancestor.classList.add('mobile-menu__pane--active-trail');
            ancestor = document.getElementById('mobile-menu-pane-' + ancestor.getAttribute('data-parent'));
        }

        if (activePane !== null) {
            activePane.classList.remove('mobile-menu__pane--active');
        }

        pane.classList.remove('mobile-menu__pane--active-trail');
        pane.classList.add('mobile-menu__pane--active');

        // Toggle HB-logo and back button in mobile-menu__topbar
        const backButton = document.querySelector('.mobile-menu__topbar__back');
        const parentId = pane.getAttribute('data-parent');

        backButton.classList.toggle('mobile-menu__topbar__back--hidden', parentId === '0');

        if (parentId !== '0') {
            // Update attributes for back button
            const paneId = 'mobile-menu-pane-' + parentId;
            backButton.setAttribute('data-target', parentId);
            backButton.setAttribute('aria-controls', paneId);
            backButton.setAttribute('href', '#' + paneId);
        }

        pane.focus();

        // Set click event for close button
        this.closeMenuButtonNode = document.getElementById('mobile-menu-close');

        this.closeMenuButtonNode.addEventListener('click', event => {
            event.preventDefault();
            this.closeMenu();
        });
    }

    /**
     * Loads a menuPane through ajax.
     * @private
     */
    _loadMenu(id) {
        const requestUrl = this.ajaxPath + (this.ajaxPath.includes('?') ? '&' : '?') + 'id=' + id;

        axios.get(requestUrl)
            .then(response => {
                const data = response.data.d || response.data;

                // This is needed to get ajax back links to animate correctly.
                const activePane = this.menuNode.querySelector('.mobile-menu__pane--active');
                const parentId = activePane && Number(activePane.getAttribute('data-parent'));

                if (parentId === data.id) {
                    data.classlist = 'mobile-menu__pane--active-trail';
                    this._renderMenuPane(data, 'AfterBegin');
                } else {
                    this._renderMenuPane(data);
                }

                setTimeout(() => this._openMenuPane('mobile-menu-pane-' + data.id), 10);

            }).catch(() => {
                console.error('Error occured, could not load menu pane. Please try again later.');
            }).then(() => {
                const loadingLink = this.menuNode.querySelector('.is-loading');
                loadingLink && loadingLink.classList.remove('is-loading');
            });
    }
}
